import { useEffect, useState } from "react";
import { TabContext, TabPanel } from "@mui/lab";
import { Stack, Tab, Tabs, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFiltersRegistry } from "../../../../../../context/FiltersProvider";
import {
	RootCauseListPayload,
	RootCauseWidgetEmissionFactorChangesTotalDTO,
} from "../../../../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../../../../datasources/networking/apiRoutes";
import { useReadResource } from "../../../../../../hooks/useResource";
import { CardDelta, CardDeltaProps } from "../../components/CardDelta";
import { getTransportTypeIcon } from "../AggregateScenario/data";
import { ChangeEmissionTab } from "./components/ChangeEmissionTab";

type ChangesFmissionFactorsProps = {
	onWarningsChange?: (warnings?: string[]) => void;
};

export const ChangesFmissionFactors = ({ onWarningsChange }: ChangesFmissionFactorsProps) => {
	const { t } = useTranslation("whatif.rootcause");
	const { rootCauseState } = useFiltersRegistry();

	const { data, isLoading } = useReadResource<RootCauseListPayload, RootCauseWidgetEmissionFactorChangesTotalDTO>(
		{
			route: ApiRoutes.RootCauseWidjetEmissionsChangeTotal,
		},
		{
			readMethod: "post",
		},
		{ ...rootCauseState.payload }
	);

	useEffect(() => {
		onWarningsChange?.(data?.warnings ?? []);
	}, [data?.warnings]);

	const [value, setValue] = useState(1);

	const handleChange = (event: React.SyntheticEvent, newValue: number) => {
		setValue(newValue);
	};

	const tabs = [
		{
			value: 1,
			label: t("root cause results/tabs/road"),
			title: t("root cause results/changes in emissions/transport titles/road"),
			transportType: t("root cause results/changes in emissions/baseline comparison data/road"),
			route: ApiRoutes.RootCauseWidjetEmissionsChangeRoad,
		},
		{
			value: 2,
			label: t("root cause results/tabs/train"),
			title: t("root cause results/changes in emissions/transport titles/train"),
			transportType: t("root cause results/changes in emissions/baseline comparison data/train"),
			route: ApiRoutes.RootCauseWidjetEmissionsChangeTrain,
		},
		{
			value: 3,
			label: t("root cause results/tabs/ship"),
			title: t("root cause results/changes in emissions/transport titles/ship"),
			transportType: t("root cause results/changes in emissions/baseline comparison data/ship"),
			route: ApiRoutes.RootCauseWidjetEmissionsChangeShip,
		},
		{
			value: 4,
			label: t("root cause results/tabs/air"),
			title: t("root cause results/changes in emissions/transport titles/air"),
			transportType: t("root cause results/changes in emissions/baseline comparison data/air"),
			route: ApiRoutes.RootCauseWidjetEmissionsChangeAir,
		},
		{
			value: 5,
			label: t("root cause results/tabs/waterways"),
			title: t("root cause results/changes in emissions/transport titles/waterways"),
			transportType: t("root cause results/changes in emissions/baseline comparison data/waterways"),
			route: ApiRoutes.RootCauseWidjetEmissionsChangeWaterways,
		},
	];

	const deltaCards: CardDeltaProps[] = [
		{
			title: t("root cause results/changes in emissions/total/emission factor"),
			value: data?.co2TotalModeDelta.value ?? "0",
			measure: data?.co2TotalModeDelta.measure ?? "-",
		},
		{
			title: t("root cause results/changes in emissions/total/energy usage"),
			value: data?.co2EnergyKgDelta.value ?? "0",
			measure: data?.co2EnergyKgDelta.measure ?? "-",
		},
		{
			title: t("root cause results/changes in emissions/total/energy carriers mix"),
			value: data?.co2FuelKgDelta.value ?? "0",
			measure: data?.co2FuelKgDelta.measure ?? "-",
		},
	];

	return (
		<Stack gap={2}>
			<Typography variant="body2">{t("root cause results/changes in emissions sub")}</Typography>
			<Stack direction="row" spacing={2}>
				{deltaCards.map((card) => (
					<CardDelta
						key={card.title}
						title={card.title}
						value={card.value}
						measure={card.measure}
						isLoading={isLoading}
					/>
				))}
			</Stack>
			<TabContext value={value}>
				<Stack width="100%" p={0} spacing={2} gap={2}>
					<Tabs value={value} onChange={handleChange}>
						{tabs.map((tab) => (
							<Tab
								key={tab.value}
								icon={getTransportTypeIcon(tab.value)}
								iconPosition="start"
								label={tab.label}
								value={tab.value}
							/>
						))}
					</Tabs>

					<Stack>
						{tabs.map((tab) => (
							<TabPanel key={tab.value} value={tab.value} sx={{ p: 0 }}>
								<ChangeEmissionTab
									route={tab.route}
									title={tab.title}
									transportType={tab.transportType}
								/>
							</TabPanel>
						))}
					</Stack>
				</Stack>
			</TabContext>
		</Stack>
	);
};
